import { Component, Input, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { environment } from '@environments/environment';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ApiTransactionService } from '@services/api-transaction.service';
import { SessionManager } from '@services/session-manager';
import { SharedService } from '@services/shared.service';
import { UploadService } from '@services/upload.service';
import { CustomerCardIssueModel } from '@shared/models/CardModel';
import { CardIssueStatus, CardIssueType } from '@shared/models/Enums';
import { TransactionModel } from '@shared/models/TransactionModel';
import { WebcamImage } from 'ngx-webcam';
import { Observable, Subject, Subscription } from 'rxjs';

@Component({
    selector: 'app-verify-card',
    templateUrl: './verify-card.component.html',
    styleUrls: ['./verify-card.component.scss']
})
export class VerifyCardComponent implements OnInit {

    @Input() issueTypes: { type: CardIssueType }[] = [];

    @ViewChild('showVerifyModal', { static: true }) verifyModal: TemplateRef<any>;

    sale: TransactionModel;

    // Image
    selfieSizeError = '';
    selfieLogoURL = '';

    cardSizeError = '';
    cardLogoURL = '';

    showCameraForSelfie = false;
    showCameraForCard = false;

    triggerForSelfie: Subject<void> = new Subject<void>();
    triggerForCard: Subject<void> = new Subject<void>();

    private verifyCardModalSubscription: Subscription = null;

    constructor(public sharedService: SharedService,
        public apiTransactionService: ApiTransactionService,
        private uploadService: UploadService,
        private modalService: NgbModal) { }

    ngOnInit(): void {

        this.verifyCardModalSubscription = this.sharedService.showVerifyCardModal.subscribe(sale => {

            if (sale) {

                this.sale = sale;
                
                this.modalService.open(this.verifyModal, {
                    keyboard: false,
                    size: 'xs', windowClass: 'modal-full'
                });
            }
        });
    }

    ngOnDestroy() {

        if (this.verifyCardModalSubscription) { this.verifyCardModalSubscription.unsubscribe(); }
    }

    onClose() {

        this.modalService.dismissAll();
    }

    onSendImages() {

        if (!this.cardLogoURL || !this.selfieLogoURL) {

            this.sharedService.showErrorAlert(null, 'Tenes que subir las dos imágenes');
            return
        }

        let verifyCard: CustomerCardIssueModel;

        this.sharedService.setLoading(true);

        if (this.sale.customerCard.issues?.length) {

            // Mas reciente
            verifyCard = this.sale.customerCard.issues.sort((a, b) => b.id - a.id)[0];

            verifyCard.cardImageURL = this.cardLogoURL;
            verifyCard.idImageURL = this.selfieLogoURL;
            verifyCard.status = CardIssueStatus.Pending

        } else {

            // Verificar si los givis comprados infringen algún trigger de fraude
            const myGivis = SessionManager.getMyGivis();
            if (myGivis.length == 0) {
                myGivis.push(this.sale);
            }
            const issues = SessionManager.getSession().verifyGiviIssues(myGivis);
            const issue = issues.find(x => x.token == this.sale.cardToken);

            verifyCard = new CustomerCardIssueModel({
                token: this.sale.customerCard.token,
                customerId: this.sale.customerId,
                cardImageURL: this.cardLogoURL,
                idImageURL: this.selfieLogoURL,
                type: issue.type,
                status: CardIssueStatus.Pending
            });
        }

        this.apiTransactionService.putVerifyCard(verifyCard)
            .then(cardIssueResult => {

                // change the father value
                this.sale.customerCard.issues = this.sale.customerCard.issues ?? [];
                const issueIndex = this.sale.customerCard.issues.findIndex(x => x.id == cardIssueResult.id);
                if (issueIndex >= 0) {
                    this.sale.customerCard.issues[issueIndex] = cardIssueResult;
                } else {
                    this.sale.customerCard.issues.push(cardIssueResult);
                }

                this.sharedService.showSuccessAlert('Imágenes enviadas', 'Nuestro equipo revisará las imágenes y podrás utilizar el Givi ');
                this.sharedService.setShowVerifyCardModal(null);
                this.modalService.dismissAll();

                this.sharedService.setLoading(false);
            }).catch(err => {

                this.sharedService.showErrorAlert(null, 'Error al subir las imágenes');

                this.sharedService.setLoading(false);
            });
    }

    onShowCameraForSelfie() {

        this.showCameraForCard = false;
        this.showCameraForSelfie = !this.showCameraForSelfie
    }

    onShowCameraForCard() {

        this.showCameraForSelfie = false;
        this.showCameraForCard = !this.showCameraForCard
    }

    triggerCaptureForSelfie(): void {
        this.triggerForSelfie.next();
    }

    triggerCaptureForCard(): void {
        this.triggerForCard.next();
    }

    get triggerObservableForSelfie(): Observable<void> {
        return this.triggerForSelfie.asObservable();
    }

    get triggerObservableForCard(): Observable<void> {
        return this.triggerForCard.asObservable();
    }

    uploadCardPhoto(webcamImage: WebcamImage): void {

        // Convertir dataURL a Blob
        const imageBlob = this.dataURItoBlob(webcamImage.imageAsDataUrl);
        const imageFile = new File([imageBlob], 'photo.png', { type: 'image/png' });

        // Validar tamaño del archivo
        if (imageFile.size > 1048576) {
            this.cardSizeError = 'Tamaño debe ser menor o igual a 1MB.';
            return;
        }

        // Configuración de AWS S3 Bucket, suponiendo que estas variables están declaradas
        let awsFileKey = '';
        let randomNumber = Math.floor(Math.random() * 100000);

        if (environment.production) {
            awsFileKey = 'CustomerCard_' + randomNumber + 'M' + this.sale.customerCard.masked + 'C' + this.sale.customerCard.customerId + '.png';
        } else {
            awsFileKey = 'CustomerCard_' + randomNumber + 'M' + this.sale.customerCard.masked + 'C' + this.sale.customerCard.customerId + '_DEMO.png';
        }

        this.sharedService.setLoading(true);

        // Usar el servicio de subida para subir la imagen al bucket de AWS S3
        this.uploadService.customerCardUpload(imageFile, awsFileKey).then(result => {

            this.cardLogoURL = result.Location;
            this.sharedService.setLoading(false);
        }).catch(err => {
            if (err.code?.indexOf('NetworkingError')) {
                this.cardSizeError = 'Error de red al subir la imagen';
            }
            this.sharedService.setLoading(false);
        });
    }

    uploadSelfiePhoto(webcamImage: WebcamImage): void {

        // Convertir dataURL a Blob
        const imageBlob = this.dataURItoBlob(webcamImage.imageAsDataUrl);
        const imageFile = new File([imageBlob], 'selfie.png', { type: 'image/png' });

        // Validar tamaño del archivo
        if (imageFile.size > 1048576) {
            this.selfieSizeError = 'Tamaño debe ser menor o igual a 1MB.';
            return;
        }

        // Configuración de AWS S3 Bucket
        let awsFileKey = '';
        let randomNumber = Math.floor(Math.random() * 100000);

        if (environment.production) {
            awsFileKey = 'CustomerSelfie_' + randomNumber + 'M' + this.sale.customerCard.masked + 'C' + this.sale.customerCard.customerId + '.png';
        } else {
            awsFileKey = 'CustomerSelfie_' + randomNumber + 'M' + this.sale.customerCard.masked + 'C' + this.sale.customerCard.customerId + '_DEMO.png';
        }

        this.sharedService.setLoading(true);

        // Usar el servicio de subida para subir la imagen al bucket de AWS S3
        this.uploadService.customerCardUpload(imageFile, awsFileKey).then(result => {
            this.selfieLogoURL = result.Location;
            this.sharedService.setLoading(false);
        }).catch(err => {
            if (err.code?.indexOf('NetworkingError')) {
                this.selfieSizeError = 'Error de red al subir la imagen';
            }
            this.sharedService.setLoading(false);
        });
    }

    // Función auxiliar para convertir dataURL a Blob
    private dataURItoBlob(dataURI: string): Blob {
        const byteString = atob(dataURI.split(',')[1]);
        const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteString.length; i++) {
            ia[i] = byteString.charCodeAt(i);
        }
        return new Blob([ab], { type: mimeString });
    }
}
